import React, { useState, useEffect } from 'react';
// import { useTimer } from '../contexts/TimerContext';
import { api } from '../config/api';
import './OTPEntryPopup.css';

const OTPEntryPopup = ({ 
  isOpen, 
  onClose, 
  onOTPVerified, 
  phoneNumber,
  onResendOTP,
  flow = 'login',        // ✅ NEW (login | register)
  formData = null        // ✅ NEW (used only for register)
}) => {
  // const { formattedTime, isExpired, isActive, startTimer } = useTimer();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  const inputRefs = [
    React.useRef(null), React.useRef(null), React.useRef(null),
    React.useRef(null), React.useRef(null), React.useRef(null)
  ];

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  /* -------------------- EFFECTS -------------------- */

  // useEffect(() => {
  //   if (isOpen && !isActive) {
  //     startTimer();
  //   }
  // }, [isOpen, isActive, startTimer]);

  // useEffect(() => {
  //   if (isExpired && isOpen) {
  //     showError('Session expired! Please start over.');
  //     onClose();
  //   }
  // }, [isExpired, isOpen, onClose]);

  useEffect(() => {
    if (isOpen && inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, [isOpen]);

  /* -------------------- INPUT HANDLERS -------------------- */

  const handleInputChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    if (index === 5 && value && newOtp.every(d => d !== '')) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => (newOtp[i] = d));
      setOtp(newOtp);
      handleVerifyOTP(pastedData);
    }
  };

  /* -------------------- VERIFY OTP (ONLY CHANGE) -------------------- */

  const handleVerifyOTP = async (otpString) => {
    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
  
    try {
      setIsVerifying(true);
      setError(null);
  
      const payload =
        flow === 'register'
          ? {
              flow: 'register',
              email: formData?.email,
            }
          : undefined;
  
      const result = await api.verifyOTP(phoneNumber, otpString, payload);
  
      if (!result.verified) {
        setError(result.error || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs[0].current?.focus();
        return;
      }
  
      // ✅ Call the callback
      onOTPVerified({
        ...(result.user || {}),
        verified: true,
      });
  
      // 🔥 ALSO CLOSE THIS POPUP
      onClose();
  
    } catch (err) {
      setError(err.message || 'Failed to verify OTP.');
      setOtp(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  /* -------------------- SUBMIT -------------------- */

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length === 6) handleVerifyOTP(otpString);
  };

  const handleResendOTP = async () => {
    try {
      setError(null);
      await api.resendOTP(phoneNumber);
      setOtp(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
      showError('OTP has been resent to your registered email address');
    } catch (err) {
      setError(err.message || 'Failed to resend OTP.');
    }
  };

  if (!isOpen) return null;

  /* -------------------- UI (UNCHANGED) -------------------- */

  return (
    <div className="otp-entry-overlay">
      <div className="otp-entry-popup">
        <button className="close-btn" onClick={onClose}>✕</button>

        {/* <div className="timer-display">
          {isExpired ? 'Session Expired' : `${formattedTime} Mins Remaining`}
        </div> */}

        <div className="simple-hero">
          <div className="floating-elements">
            <div className="floating-icon">🔐</div>
            <div className="floating-icon">📧</div>
            <div className="floating-icon">📱</div>
          </div>
        </div>

        <div className="popup-content">
          <h2 className="popup-title">Enter OTP</h2>
          <p className="popup-subtitle">
            We've sent a 6-digit OTP to your registered email address
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="otp-form">
            <div className="otp-input-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="otp-digit-input"
                  disabled={isVerifying}
                />
              ))}
            </div>

            <button
              type="submit"
              className="proceed-btn"
              disabled={isVerifying || otp.join('').length !== 6}
            >
              {isVerifying ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>

          <div className="resend-section">
            <p className="resend-text">Didn't receive the OTP?</p>
            <button
              type="button"
              className="resend-btn"
              onClick={handleResendOTP}
              disabled={isVerifying}
            >
              Resend OTP
            </button>
          </div>
        </div>
      </div>

      {showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content">
            <div className="error-popup-message">{errorMessage}</div>
            <button onClick={() => setShowErrorPopup(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTPEntryPopup;
